import { Moment } from 'moment';
import * as moment from 'moment-timezone';

/**
 * A follow-up's `actionOn` is a calendar day — the day a concierge should chase — but it
 * is stored and sent as an *instant*, anchored to whichever timezone the concierge who
 * set it was in. HQ writes it through `AzcMoment.format`, which emits the setter's UTC
 * offset, so a chase set for the 3rd in Melbourne is stored as `2026-09-02T14:00:00Z`.
 *
 * Rendering that in the reader's own timezone shows the previous day to anyone west of
 * the setter: the same instant is the 2nd at 22:00 in Perth. Reading every value in one
 * business timezone instead makes the list say the same thing to everyone, which is what
 * a shared work queue needs.
 *
 * Which zone is chosen matters only for where a chase is *set*, not where it is read:
 * the day is correct as long as the business zone is no further west than the setter.
 * Melbourne covers everywhere HQ is used to set chases — Perth (+8) eastward, and the
 * US/CA locales, whose midnight falls mid-afternoon in Melbourne. The known gap is New
 * Zealand: NZ midnight (+12/+13) is still the previous evening in Melbourne, so a chase
 * set there would read a day early for everyone, including in NZ.
 */
export const FollowUpTimezone = 'Australia/Melbourne';

export class FollowUpDate {
	/**
	 * The calendar day an `actionOn` value means, identical for every reader.
	 *
	 * `moment.utc` first, so that a bare `2026-09-02T14:00:00` and a tagged
	 * `2026-09-02T14:00:00+00:00` are read as the same instant. Both shapes are on the
	 * wire: core-api tags `actionOn`, the legacy events API does not.
	 */
	static day(value: Moment | string): Moment {
		return moment.utc(value).tz(FollowUpTimezone).startOf('day');
	}

	/** Today, in the same terms as {@link day}, for comparing one against the other. */
	static today(): Moment {
		return moment.tz(FollowUpTimezone).startOf('day');
	}

	/**
	 * Lower filter bound for a `YYYY-MM-DD` picked in the UI, as an instant so that
	 * core-api uses it verbatim rather than reading it as a UTC day. Keeps the days
	 * filtered the same as the days displayed.
	 */
	static startOfDay(day: string): string {
		return moment.tz(day, FollowUpTimezone).startOf('day').toISOString();
	}

	/** Upper filter bound, as {@link startOfDay}. */
	static endOfDay(day: string): string {
		return moment.tz(day, FollowUpTimezone).endOf('day').toISOString();
	}
}
